import React, { useEffect, useState } from "react";
import { useForm, Controller, FormProvider } from "react-hook-form";
import { SubmitButton } from "components/common/buttons/SubmitButton";
import ErrorMessage from "components/common/ErrorMessage";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import { feedbackVerfication, submitFeedback } from "slices/appointmentSlice";
import { useDispatch, useSelector } from "react-redux";
import FullPageLoader from "components/common/FullPageLoader";
import { appointmentsSelector } from "slices/appointmentSlice";

const STAR_LABELS = ["Poor", "Fair", "Good", "Very Good", "Excellent"];

const getFeedbackStatus = (message = "") => {
  const normalizedMessage = message.toLowerCase();

  if (
    normalizedMessage.includes("already taken") ||
    normalizedMessage.includes("already submitted") ||
    normalizedMessage.includes("feedback taken")
  ) {
    return {
      title: "Feedback Already Taken",
      message: message || "This feedback has already been submitted.",
      variant: "success",
    };
  }

  if (
    normalizedMessage.includes("invalid") ||
    normalizedMessage.includes("expired")
  ) {
    return {
      title: "Invalid Feedback Link",
      message: message || "This feedback link is invalid or expired.",
      variant: "error",
    };
  }

  return null;
};

const StarRating = ({ value, onChange }) => {
  const [hovered, setHovered] = useState(0);
  const active = hovered || Number(value);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 12,
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 14,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {STAR_LABELS.map((label, i) => {
          const star = i + 1;
          return (
            <span
              key={star}
              title={`${star} - ${label}`}
              onClick={() => onChange(String(star))}
              onMouseEnter={() => setHovered(star)}
              onMouseLeave={() => setHovered(0)}
              style={{
                cursor: "pointer",
                fontSize: 52,
                lineHeight: 1,
                color: star <= active ? "#f5a623" : "#d1d5db",
                transition: "color 0.15s, transform 0.15s",
                userSelect: "none",
              }}
            >
              {"\u2605"}
            </span>
          );
        })}
      </div>
      {value && (
        <span style={{ color: "#6b7280", fontSize: 16, textAlign: "center" }}>
          {value} - {STAR_LABELS[Number(value) - 1]}
        </span>
      )}
    </div>
  );
};

const STATUS_ICON_STYLES = {
  success: {
    backgroundColor: "#dcfce7",
    color: "#16a34a",
    symbol: "\u2713",
  },
  error: {
    backgroundColor: "#fee2e2",
    color: "#dc2626",
    symbol: "\u2715",
  },
};

const FeedbackForm = ({ logoUrl }) => {
  const { loader } = useSelector(appointmentsSelector);
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const dispatch = useDispatch();
  const [feedbackResult, setFeedbackResult] = useState(null);
  const [appointmentInfo, setAppointmentInfo] = useState(null);

  const methods = useForm({
    defaultValues: {
      rating: "",
      comments: "",
      suggestions: "",
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = methods;

  useEffect(() => {
    if (!token) {
      setFeedbackResult({
        title: "Invalid Feedback Link",
        message: "Invalid feedback link.",
        variant: "error",
      });
      return;
    }

    dispatch(
      feedbackVerfication(token, (success, message, data) => {
        const feedbackStatus = getFeedbackStatus(message);

        if (feedbackStatus) {
          setFeedbackResult(feedbackStatus);
          return;
        }

        if (!success) {
          toast.error(message || "Invalid or expired feedback link");
          setFeedbackResult({
            title: "Invalid Feedback Link",
            message: message || "Invalid or expired feedback link.",
            variant: "error",
          });
          return;
        }

        // store appointment info if provided by API
        if (data && data.appointment) {
          setAppointmentInfo(data.appointment);
        }
      })
    );
  }, [token, dispatch]);

  const onSubmit = (data) => {
    const payload = {
      token,
      ...data,
    };

    dispatch(
      submitFeedback(payload, (success, message) => {
        if (!success) {
          const feedbackStatus = getFeedbackStatus(message);

          if (feedbackStatus) {
            setFeedbackResult(feedbackStatus);
            return;
          }

          toast.error(message || "Failed to submit feedback");
          return;
        }

        setFeedbackResult({
          title: "Feedback Taken",
          message: message || "Thank you for your feedback!",
          variant: "success",
        });
      })
    );
  };

  if (loader) {
    return <FullPageLoader />;
  }
  return (
    <>
      {/* {loader && <FullPageLoader />} */}
      <div className="mb-5" style={{ minHeight: "100vh"}}>
        <div className="d-flex justify-content-center align-items-center flex-column h-100">
          <div>
            <img
              src="/images/logo-fill-second.png"
              alt="react-logo test"
              style={{ height: "auto", width: "397px" }}
            />
          </div>
          <FormProvider {...methods}>
            {feedbackResult ? (
              (() => {
                const statusIcon =
                  STATUS_ICON_STYLES[feedbackResult.variant] ||
                  STATUS_ICON_STYLES.error;

                return (
                  <div
                    className="card_table p-4 w-100"
                    style={{
                      maxWidth: 600,
                      margin: "0 auto",
                      minHeight: 320,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      textAlign: "center",
                      gap: '10px',
                    }}
                  >
                    {logoUrl && (
                      <div className="text-center mb-4">
                        <img
                          src={logoUrl}
                          alt="Logo"
                          style={{ maxHeight: 80, objectFit: "contain" }}
                        />
                      </div>
                    )}
                    <div
                      style={{
                        width: 72,
                        height: 72,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: statusIcon.backgroundColor,
                        color: statusIcon.color,
                        fontSize: 38,
                        fontWeight: 700,
                        marginBottom: 20,
                      }}
                    >
                      {statusIcon.symbol}
                    </div>
                    <div>
                      <h2
                        style={{
                          marginBottom: 12,
                          fontSize: "30px",
                          fontWeight: "bold",
                        }}
                      >
                        {feedbackResult.title}
                      </h2>
                      <p style={{ margin: 0, color: "#6b7280", fontSize: 16 }}>
                        {feedbackResult.message}
                      </p>
                    </div>
                  </div>
                );
              })()
            ) : (
              <form
                className="card_table p-4 w-100"
                onSubmit={handleSubmit(onSubmit)}
                style={{ maxWidth: 600, margin: "0 auto" }}
              >
                {logoUrl && (
                  <div className="text-center mb-4">
                    <img
                      src={logoUrl}
                      alt="Logo"
                      style={{ maxHeight: 80, objectFit: "contain" }}
                    />
                  </div>
                )}

                {appointmentInfo && (
                  <div
                    style={{
                      width: '100%',
                      maxWidth: 600,
                      margin: '0 auto 16px',
                      borderRadius: 12,
                      padding: '18px',
                      background: '#ebebeb',
                      // color: '#fff',
                    }}
                  >
                    <h3 style={{ margin: 0, marginBottom: 8, fontWeight: 'bold' }}>Appointment Detail</h3>
                    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                      <div style={{ flex: '1 1 240px', minWidth: 220 }}>
                        <div style={{ marginBottom: 8 }}>
                          <p style={{marginBottom: '5px'}}>App ID:</p>{' '}
                          <span>
                            {appointmentInfo?.appid ?? `#${appointmentInfo?.id}`}
                          </span>
                        </div>
                        <div style={{ marginBottom: 8 }}>
                          <p style={{marginBottom: '5px'}}>Service Type:</p>{' '}
                          <span>{appointmentInfo?.type ?? 'N/A'}</span>
                        </div>
                      </div>

                      <div style={{ flex: '1 1 200px', minWidth: 180 }}>
                        <div style={{ marginBottom: 8 }}>
                          <p style={{marginBottom: '5px'}}>Language:</p>{' '}
                          <span>{appointmentInfo?.language_name ?? 'N/A'}</span>
                        </div>
                        <div style={{ marginBottom: 8 }}>
                          <p style={{marginBottom: '5px'}}>Vendor Name:</p>{' '}
                          <span>{appointmentInfo?.vendor_name ?? 'N/A'}</span>
                        </div>
                      </div>
                    </div>

                    <div style={{ marginTop: 12 }}>
                      <a
                        className="main_button text-center"
                        style={{ width: '100%', display: 'inline-block' }}
                        href={`${window.location.origin}/user/appointments/view/${appointmentInfo?.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        VIEW DETAILED APPOINTMENT
                      </a>
                    </div>
                  </div>
                )}

                <div
                  className="row-primary flex-column"
                >
                  <h2>Rating *</h2>
                  <div className="w-100 d-flex justify-content-center">
                    <Controller
                      name="rating"
                      control={control}
                      rules={{ required: "Please select a rating" }}
                      render={({ field }) => (
                        <StarRating value={field.value} onChange={field.onChange} />
                      )}
                    />
                  </div>
                  {errors?.rating?.message && (
                    <div style={{ marginBottom: 10 }}>
                      <ErrorMessage text={errors?.rating?.message} />
                    </div>
                  )}
                </div>

                <div className="row-primary flex-column">
                  <h2>Comments</h2>
                  <div className="w-100">
                    <Controller
                      name="comments"
                      control={control}
                      render={({ field }) => (
                        <textarea
                          className="input-in type_inp"
                          placeholder="Write your comments here"
                          rows={4}
                          {...field}
                        />
                      )}
                    />
                  </div>
                </div>

                <div className="row-primary flex-column">
                  <h2>Suggestions</h2>
                  <div className="w-100">
                    <Controller
                      name="suggestions"
                      control={control}
                      render={({ field }) => (
                        <textarea
                          className="input-in type_inp"
                          placeholder="Any suggestions for improvement"
                          rows={4}
                          {...field}
                        />
                      )}
                    />
                  </div>
                </div>

                <div className="Form-footer footer_fullpage mt-4">
                  <SubmitButton
                    btnText="Submit Feedback"
                    className="w-100"
                    disabled={loader}
                  />
                </div>
              </form>
            )}
          </FormProvider>
        </div>
      </div>
    </>
  );
};

export default FeedbackForm;
